import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, MapPin, Plane, TrainFront } from 'lucide-react';
import { CORRIDORS } from '../data/corridors';
import './HowToReach.css';

/**
 * /how-to-reach — hub page listing every corridor guide.
 * SEO role: internal-linking hub that passes equity to corridor pages
 * and gives crawlers a single discovery path for the whole cluster.
 */
const HowToReachIndex = () => {
    const entries = Object.entries(CORRIDORS);

    useEffect(() => {
        document.title = 'How to Reach Guides — Trains, Flights & Cabs | RouteChef';
        const ensureMeta = (attr, key, content) => {
            let el = document.head.querySelector(`meta[${attr}="${key}"]`);
            if (!el) {
                el = document.createElement('meta');
                el.setAttribute(attr, key);
                document.head.appendChild(el);
            }
            el.setAttribute('content', content);
        };
        ensureMeta('name', 'description',
            'Step-by-step travel guides for reaching Indian destinations — nearest airports and railway stations, cab distances, journey times and live door-to-door fares.');

        const ld = document.createElement('script');
        ld.type = 'application/ld+json';
        ld.id = 'howtoreach-index-jsonld';
        ld.text = JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            itemListElement: entries.map(([slug, c], i) => ({
                '@type': 'ListItem',
                position: i + 1,
                name: `How to reach ${c.to.name} from ${c.from.name}`,
                url: `https://routechef.com/how-to-reach/${slug}`,
            })),
        });
        document.head.appendChild(ld);
        return () => document.getElementById('howtoreach-index-jsonld')?.remove();
    }, [entries]);

    return (
        <div className="htr-page">
            <header className="htr-hero">
                <nav className="htr-breadcrumb" aria-label="Breadcrumb">
                    <Link to="/">Home</Link>
                    <ChevronRight size={14} />
                    <span className="htr-crumb-current">How to Reach</span>
                </nav>
                <h1>How to Reach: Travel Guides</h1>
                <p className="htr-subtitle">
                    Practical, door-to-door guides for India's trickiest destinations — nearest airports and
                    railway stations, cab distances, and live fares for every leg of the journey.
                </p>
            </header>

            <main className="htr-content">
                <div className="htr-index-grid">
                    {entries.map(([slug, c]) => (
                        <Link key={slug} to={`/how-to-reach/${slug}`} className="htr-index-card">
                            <div className="htr-index-route">
                                <MapPin size={17} />
                                <span>{c.from.name} <ChevronRight size={15} className="htr-index-arrow" /> {c.to.name}</span>
                            </div>
                            <p className="htr-index-desc">{c.quickAnswer}</p>
                            <div className="htr-index-meta">
                                <span><Plane size={14} /> {c.gateways.find((g) => g.type === 'Airport')?.name || '—'}</span>
                                <span><TrainFront size={14} /> {c.gateways.find((g) => g.type !== 'Airport')?.name || '—'}</span>
                            </div>
                            <span className="htr-index-cta">Read the guide <ChevronRight size={15} /></span>
                        </Link>
                    ))}
                </div>

                <p className="htr-disclaimer" style={{ textAlign: 'center' }}>
                    More corridors are added regularly. Can't find yours?{' '}
                    <Link to="/search-form" style={{ color: '#FF4E01' }}>Search any route directly</Link>.
                </p>
            </main>
        </div>
    );
};

export default HowToReachIndex;
